import { Day, State, rangePosition, type RangePosition } from '$/epg/programs'
import { type EpgInfo, type LiveChannel } from '@setplex/tria-api'
import {
  DATE_FORMATS,
  formatDateToLabelInfo,
  formatStartTime,
} from '~/shared/helpers/date'
import {
  type EpgSidebarInfo,
  type EpgSidebarProgramInfo,
  type EpgSidebarProgramLocalizedInfo,
} from './index.h'

export const formatProgramToView = ({
  channel,
  program,
}: {
  channel: Readonly<LiveChannel>
  program: Readonly<EpgInfo>
}): EpgSidebarProgramInfo => ({
  actors: program.actors,
  categories: program.categories,
  channelName: `${channel.number}. ${channel.title}`,
  description: program.description,
  directors: program.directors,
  episode: program.episode,
  icon: program.icon,
  logoUrl: channel.logo,
  subtitle: program.subtitle,
  date: program.date,
  rating: program.rating,
  title: program.title,
  actualStartMs: program.actualStartMs,
  actualStopMs: program.actualStopMs,
  actualStartSec: program.actualStartSec,
  actualStopSec: program.actualStopSec,
  isEmpty: program.isCatchup ? false : program.isEmpty,
  isCatchup: program.isCatchup,
  catchUp: program.catchUp,
})

export const formatToView = (
  info: EpgSidebarProgramLocalizedInfo
): EpgSidebarInfo => ({
  actors: info.actors,
  categories: info.categories,
  channelName: info.channelName,
  description: info.description,
  directors: info.directors,
  episode: info.episode,
  icon: info.icon,
  isLive: info.isLive,
  isEmpty: info.isEmpty,
  labels: info.labels,
  logoUrl: info.logoUrl,
  subtitle: info.subtitle,
  rating: info.rating,
  title: info.title,
  isCatchup: info.isCatchup,
  actualStartMs: info.actualStartMs,
  actualStopMs: info.actualStopMs,
  actualStartSec: info.actualStartSec,
  actualStopSec: info.actualStopSec,
  catchUp: info.catchUp,
})

const formatStartEndSidebar = ({
  actualStartMs,
  actualStopMs,
  position,
}: Readonly<{
  actualStartMs: number
  actualStopMs: number
  position: RangePosition
}>): string[] => {
  if (!actualStartMs || !actualStopMs) return []

  const startEndLabel = formatDateToLabelInfo(actualStartMs, actualStopMs)

  // for live program format labels as [live, start-end, left time]
  if (position.state === State.Ongoing) {
    return [startEndLabel]
  }

  // for today future program format labels as [today, start-end]
  if (position.state === State.Upcoming && position.starts === Day.Today) {
    return ['Today', startEndLabel]
  }

  // for tomorrow program format labels as [tomorrow, start-end]
  if (position.state === State.Upcoming && position.starts === Day.Tomorrow) {
    return ['Tomorrow', startEndLabel]
  }

  // for today past program format labels as [today, start-end]
  if (position.state === State.Elapsed && position.starts === Day.Today) {
    return ['Today', startEndLabel]
  }

  // for yesterday program format labels as [yesterday, start-end]
  if (position.state === State.Elapsed && position.starts === Day.Yesterday) {
    return ['Yesterday', startEndLabel]
  }

  // for all other cases (future) format labels as [date, start-end]
  const date = formatStartTime(actualStartMs, DATE_FORMATS.MMM_D)
  return [date, startEndLabel]
}

export const formatProgramToLocalizedView =
  ({ now }: { now: number }) =>
  (info: EpgSidebarProgramInfo): EpgSidebarProgramLocalizedInfo => {
    const position = rangePosition([info.actualStartMs, info.actualStopMs], now)
    const isLive = position.state === State.Ongoing

    const programTitle =
      !isLive && !info.title
        ? `Program ${(info.channelName || '').split('. ')[1]}`
        : info.title

    return {
      ...info,
      isLive,
      labels: formatStartEndSidebar({
        actualStartMs: info.actualStartMs,
        actualStopMs: info.actualStopMs,
        position,
      }),
      title: programTitle,
    }
  }
